import { useEffect, useState } from "react"
import { useParams, useNavigate, Navigate } from "react-router-dom"
import { useAtom, useSetAtom } from "jotai"
import { fetchPlanAtom, refetchPlansAtom, userAtom } from "../state"
import api from "../services/api"
import Button from "../Components/Button"
import { PLAN_USER_ROLE_LABELS } from "../constants/planUserRole"
import Spinner from "../Components/Spinner"
import { sentryService } from "../services/sentry"

const PlanInvitationAccept = () => {
  const { token } = useParams()
  const fetchPlan = useSetAtom(fetchPlanAtom)
  const fetchPlans = useSetAtom(refetchPlansAtom)
  const navigate = useNavigate()
  const [user] = useAtom(userAtom)
  const [status, setStatus] = useState("loading") // loading, ready, accepting, declining, error
  const [invitation, setInvitation] = useState(null)
  const [error, setError] = useState("")
  const [notFound, setNotFound] = useState(false)
  const [marketingOptedIn, setMarketingOptedIn] = useState(null)

  useEffect(() => {
    // Fetch invitation details
    api
      .getInvitationDetails(token)
      .then(response => {
        setInvitation(response.data)
        setMarketingOptedIn(response.data.marketing_opted_in ?? true)
        // Already accepted (e.g., auto-accepted via existing plan membership).
        // Still call acceptInvitation so the backend progression check runs,
        // then navigate to the plan.
        if (response.data.invitation?.status === 'accepted') {
          handleAccept()
          return
        }
        setStatus("ready")
      })
      .catch(error => {
        console.error("Error loading invitation:", error)
        setError(error.response?.data?.message || "Failed to load invitation details")
        setStatus("error")
        if (error.response?.status === 404) {
          setNotFound(true)
        } else if (error.response?.status === 403) {
          navigate("/", {
            state: { message: "You do not have permission to accept this invitation." },
          })
        } else {
          sentryService.captureException(error)
        }
      })
  }, [token])

  const handleAccept = () => {
    setStatus("accepting")
    const data = marketingOptedIn !== null ? { marketing_opted_in: marketingOptedIn } : {}
    api
      .acceptInvitation(token, data)
      .then(() => {
        Promise.all([fetchPlans(), fetchPlan(token)]).then(() => {
          navigate(`/plan/${token}/activities`)
        })
      })
      .catch(error => {
        console.error("Error accepting invitation:", error)
        setError(error.response?.data?.message || "Failed to accept invitation")
        setStatus("error")
      })
  }

  const handleDecline = () => {
    setStatus("declining")
    api
      .declineInvitation(token)
      .then(() => {
        fetchPlans().then(() => {
          navigate("/")
        })
      })
      .catch(error => {
        console.error("Error declining invitation:", error)
        setError(error.response?.data?.message || "Failed to decline invitation")
        setStatus("error")
      })
  }

  const getRoleLabel = role => {
    return PLAN_USER_ROLE_LABELS[role] || role
  }

  if (notFound)
    return <Navigate to="/not-found" state={{ message: "This invitation has expired or is invalid." }} replace />

  if (!invitation)
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <Spinner size={"lg"} />{" "}
      </div>
    )

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-2xl">
        <div className="mb-8 text-center">
          <img className="mx-auto mb-8 w-[200px]" src="/logo.png" alt="Logo" />
        </div>

        {status === "loading" && (
          <div className="rounded-lg bg-white p-8 text-center shadow-md">
            <Spinner size={"lg"} className="mx-auto mb-4" />
            <h2 className="mb-2 text-xl font-bold">Loading invitation...</h2>
            <p className="text-gray-600">Please wait a moment.</p>
          </div>
        )}

        {status === "ready" && invitation && (
          <div className="rounded-lg bg-white p-8 shadow-md">
            <div className="mb-6 text-center">
              <h2 className="mb-2 text-2xl font-bold">You've Been Invited!</h2>
              <p className="text-gray-600">
                {invitation.inviter.name} has invited you to participate as a{" "}
                {getRoleLabel(invitation?.invitation?.role)} in {invitation?.plan?.name}.
              </p>
            </div>

            <div className="mb-6 border-l-4 border-brandSecondary/70 bg-brandSecondary/10 p-4">
              <p className="text-sm text-brandSecondary">
                By accepting this invitation, you'll be able to collaborate on this plan with{" "}
                {invitation.inviter.name} and other members of the Mentor Circle.
              </p>
            </div>

            <div className="mb-6">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={marketingOptedIn ?? true}
                  onChange={e => setMarketingOptedIn(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded text-brandSecondary focus:ring-brandSecondary border-brandSecondary"
                />
                <span className="flex-1 text-sm text-gray-900">
                  Help our mission grow by signing up to receive Marketing Communications
                </span>
              </label>
            </div>

            <div className="flex gap-4">
              <Button
                onClick={handleAccept}
                className="flex-1 bg-green-600 hover:bg-green-700"
                disabled={status === "accepting"}
              >
                {status === "accepting" ? "Accepting..." : "Accept Invitation"}
              </Button>
              <Button
                onClick={handleDecline}
                className="flex-1 bg-red-600 hover:bg-red-700"
                disabled={status === "declining"}
              >
                {status === "declining" ? "Declining..." : "Decline"}
              </Button>
            </div>
          </div>
        )}

        {(status === "accepting" || status === "declining") && (
          <div className="rounded-lg bg-white p-8 text-center shadow-md">
            <Spinner size={"lg"} className="mx-auto mb-4" />
            <h2 className="mb-2 text-xl font-bold">
              {status === "accepting" ? "Accepting invitation..." : "Declining invitation..."}
            </h2>
            <p className="text-gray-600">Please wait a moment.</p>
          </div>
        )}

        {status === "error" && (
          <div className="rounded-lg bg-white p-8 text-center shadow-md">
            <div className="mb-4 text-6xl text-red-500">✗</div>
            <h2 className="mb-2 text-xl font-bold">Error</h2>
            <p className="mb-6 text-gray-600">{error}</p>
            <Button onClick={() => navigate("/")}>Go Home</Button>
          </div>
        )}
      </div>
    </div>
  )
}

export default PlanInvitationAccept
